/** A2 slice 3 — the SLA digest: what would post right now, last send, and a 'Send now' for the Head of Legal. */
import { useState } from "react";
import { Eye, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getSlaDigestPreview, runSlaNow } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export default function SlaDigestPanel({ canPublish }) {
  const [busy, setBusy] = useState("");
  const [digest, setDigest] = useState(null);
  const err = (e) => toast.error(errorDetailMessage(e, e.message));

  const preview = async () => {
    setBusy("preview");
    try { setDigest(await getSlaDigestPreview()); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const sendNow = async () => {
    if (!window.confirm(`Post today's digest to ${digest?.channel || "the configured channel"} now?`)) return;
    setBusy("send");
    try {
      const r = await runSlaNow(true);
      if (r.posted) toast.success(`Digest posted to ${r.channel} — ${r.stamped} new SLA breach${r.stamped === 1 ? "" : "es"} stamped`);
      else toast.error(r.skipped || r.error || "Not posted");
      await preview();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  return (
    <div className="border rounded-md p-3 space-y-2 bg-slate-50/60" data-testid="sla-digest-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={preview} disabled={!!busy} data-testid="sla-digest-preview-btn">
          {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}Preview today's digest
        </Button>
        {canPublish && (
          <Button size="sm" className="h-8 text-xs" onClick={sendNow} disabled={!!busy || !digest} title="Stamps SLA breaches and posts the digest to Slack now (uses the PUBLISHED channel)" data-testid="sla-digest-send-btn">
            {busy === "send" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send now
          </Button>
        )}
        {digest?.status && (
          <span className="text-[11px] text-slate-500" data-testid="sla-digest-status">
            {digest.status.open_breached} open action{digest.status.open_breached === 1 ? "" : "s"} breached · last posted {digest.status.last_sent_date || "never"}
            {digest.status.last_result?.error && <span className="text-rose-700"> · last error: {digest.status.last_result.error}</span>}
          </span>
        )}
      </div>
      {digest && (
        <div className="text-xs border rounded bg-white p-2 space-y-1" data-testid="sla-digest-preview">
          <div className="font-medium">{digest.title} <span className="text-slate-400 font-normal">→ {digest.channel || "no channel"} at {digest.digest_time} London{digest.enabled ? "" : " · digest OFF"}</span></div>
          {digest.rows.length === 0 && <div className="text-emerald-700">Nothing overdue, nothing breached, nothing due today.</div>}
          {digest.rows.map((r) => (
            <div key={r.owner} data-testid={`sla-digest-owner-${r.owner.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-medium">{r.owner} <span className="text-slate-500 font-normal">— {r.breached} breached · {r.overdue} overdue · {r.due_today} due today</span></div>
              <ul className="pl-3 text-[11px] text-slate-600">{r.lines.slice(0, 6).map((l, i) => <li key={i}>{l.replace(/<[^|]+\|([^>]+)>/g, "$1").replace(/[`*]/g, "")}</li>)}</ul>
            </div>
          ))}
          <div className="text-amber-800">Unassigned: {digest.unassigned.count} live legal file{digest.unassigned.count === 1 ? "" : "s"} with no owner{digest.unassigned.count ? ` (oldest ${digest.unassigned.oldest_days} days)` : ""}</div>
        </div>
      )}
    </div>
  );
}
